import React, { useState } from 'react';
import {
  RegulatoryChangeRecord,
  RegulatoryChangeLifecycle,
  RegulatoryChangeSeverity,
  ActionItemType,
} from './types';

interface Props {
  changes: RegulatoryChangeRecord[];
  onCreateChange: (data: Partial<RegulatoryChangeRecord>) => void;
  onTransitionChange: (id: string, newStatus: RegulatoryChangeLifecycle, notes?: string) => void;
  onAddAction: (changeId: string, data: {
    title: string;
    action_type: ActionItemType;
    assigned_department: string;
    assignee: string;
    due_date: string;
  }) => void;
  onUpdateAction: (actionId: string, status: string, notes?: string) => void;
}

export const RegulatoryChangeRegister: React.FC<Props> = ({
  changes,
  onCreateChange,
  onTransitionChange,
  onAddAction,
  onUpdateAction,
}) => {
  const [selectedChange, setSelectedChange] = useState<RegulatoryChangeRecord | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // New Change Form State
  const [changeRef, setChangeRef] = useState('');
  const [title, setTitle] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [severity, setSeverity] = useState<RegulatoryChangeSeverity>('MAJOR');
  const [impactAssessment, setImpactAssessment] = useState('');
  const [requiresMoc, setRequiresMoc] = useState(false);
  const [requiresDoc, setRequiresDoc] = useState(false);
  const [requiresTraining, setRequiresTraining] = useState(false);
  const [requiresAudit, setRequiresAudit] = useState(false);

  // New Action Form State
  const [actionTitle, setActionTitle] = useState('');
  const [actionType, setActionType] = useState<ActionItemType>('MANUAL_UPDATE');
  const [actionDept, setActionDept] = useState('Flight Operations');
  const [actionAssignee, setActionAssignee] = useState('');
  const [actionDueDate, setActionDueDate] = useState('');

  const handleCreateChangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!changeRef || !title || !effectiveDate) return;
    onCreateChange({
      change_reference: changeRef,
      title,
      effective_date: effectiveDate,
      severity,
      impact_assessment: impactAssessment,
      requires_moc: requiresMoc,
      requires_document_revision: requiresDoc,
      requires_training: requiresTraining,
      requires_audit: requiresAudit,
    });
    setShowCreateModal(false);
    setChangeRef('');
    setTitle('');
    setEffectiveDate('');
    setImpactAssessment('');
  };

  const handleAddActionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChange || !actionTitle || !actionAssignee || !actionDueDate) return;
    onAddAction(selectedChange.id, {
      title: actionTitle,
      action_type: actionType,
      assigned_department: actionDept,
      assignee: actionAssignee,
      due_date: actionDueDate,
    });
    setShowActionModal(false);
    setActionTitle('');
    setActionAssignee('');
    setActionDueDate('');
  };

  const handleTransition = async (changeId: string, newStatus: RegulatoryChangeLifecycle) => {
    try {
      setErrorMsg(null);
      await onTransitionChange(changeId, newStatus, 'Status updated via compliance interface');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to update lifecycle status');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Bar Card */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 className="card-title">Regulatory Changes & Closed-Loop Management</h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
              Impact assessment, work packages, and gated closure verification for regulatory amendments
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            style={{ fontSize: '13px', padding: '6px 14px' }}
          >
            + New Regulatory Change
          </button>
        </div>
      </div>

      {errorMsg && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '8px', fontSize: '13px' }}>
          ⚠️ Error: {errorMsg}
        </div>
      )}

      {/* Changes Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px' }}>
        {changes.map((chg) => (
          <div
            key={chg.id}
            className="card"
            style={{
              padding: '20px',
              border: selectedChange?.id === chg.id ? '2px solid #2563eb' : '1px solid #e2e8f0',
              transition: 'border 0.2s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span className="badge-pill badge-info" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {chg.change_reference}
                </span>
                <h4 style={{ margin: '8px 0 0 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{chg.title}</h4>
              </div>
              <span
                className={`badge-pill ${
                  chg.lifecycle_status === 'CLOSED'
                    ? 'badge-success'
                    : chg.lifecycle_status === 'IN_IMPLEMENTATION'
                    ? 'badge-info'
                    : 'badge-warning'
                }`}
              >
                {chg.lifecycle_status}
              </span>
            </div>

            <p style={{ fontSize: '13px', color: '#64748b', marginTop: '10px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {chg.impact_assessment || 'No impact assessment provided.'}
            </p>

            {/* Impact Flags Badges */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
              {chg.requires_moc ? (
                <span className="badge-pill badge-danger">MoC Required</span>
              ) : null}
              {chg.requires_document_revision ? (
                <span className="badge-pill badge-info">Document Revision</span>
              ) : null}
              {chg.requires_training ? (
                <span className="badge-pill badge-warning">Training Required</span>
              ) : null}
              {chg.requires_audit ? (
                <span className="badge-pill badge-neutral">Audit Required</span>
              ) : null}
            </div>

            {/* Action Item summary */}
            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
              <span style={{ color: '#64748b', fontWeight: 500 }}>
                Actions: {chg.actions?.filter((a) => a.status === 'COMPLETED').length || 0} /{' '}
                {chg.actions?.length || 0} Completed
              </span>
              <button
                onClick={() => setSelectedChange(chg)}
                className="btn-secondary"
                style={{ fontSize: '12px', padding: '4px 10px' }}
              >
                Manage Actions →
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Selected Change Work Packages & Verification */}
      {selectedChange && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h4 className="card-title">
                Action Work Packages & Verification Gate: {selectedChange.title} ({selectedChange.change_reference})
              </h4>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                Status: <strong style={{ color: '#2563eb' }}>{selectedChange.lifecycle_status}</strong> | Effective Date:{' '}
                {selectedChange.effective_date.slice(0, 10)}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              {selectedChange.lifecycle_status === 'DETECTED' && (
                <button
                  onClick={() => handleTransition(selectedChange.id, 'IN_IMPLEMENTATION')}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Move to Implementation
                </button>
              )}
              {selectedChange.lifecycle_status === 'IN_IMPLEMENTATION' && (
                <button
                  onClick={() => handleTransition(selectedChange.id, 'VERIFYING')}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '6px 12px', backgroundColor: '#6366f1' }}
                >
                  Submit for Verification
                </button>
              )}
              {selectedChange.lifecycle_status === 'VERIFYING' && (
                <button
                  onClick={() => handleTransition(selectedChange.id, 'CLOSED')}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '6px 12px', backgroundColor: '#10b981' }}
                >
                  Approve Closure (Close)
                </button>
              )}
              <button
                onClick={() => setShowActionModal(true)}
                className="btn-secondary"
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                + Add Action
              </button>
            </div>
          </div>

          {/* Action items table */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Action Title</th>
                  <th>Type</th>
                  <th>Assignee & Dept</th>
                  <th>Due Date</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(!selectedChange.actions || selectedChange.actions.length === 0) ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                      No action packages created yet for this change.
                    </td>
                  </tr>
                ) : (
                  selectedChange.actions.map((act) => (
                    <tr key={act.id}>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{act.title}</td>
                      <td style={{ color: '#475569', fontSize: '12px' }}>{act.action_type}</td>
                      <td style={{ color: '#475569', fontSize: '12px' }}>
                        {act.assignee} ({act.assigned_department})
                      </td>
                      <td style={{ color: '#475569', fontSize: '12px' }}>{act.due_date.slice(0, 10)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className={`badge-pill ${
                            act.status === 'COMPLETED' ? 'badge-success' : 'badge-warning'
                          }`}
                        >
                          {act.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {act.status !== 'COMPLETED' && (
                          <button
                            onClick={() => onUpdateAction(act.id, 'COMPLETED', 'Action completed successfully.')}
                            className="btn-primary"
                            style={{ padding: '3px 8px', fontSize: '11px', backgroundColor: '#10b981' }}
                          >
                            Complete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: New Regulatory Change */}
      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="card" style={{ maxWidth: '540px', width: '100%', margin: '20px' }}>
            <div className="card-header">
              <h3 className="card-title">New Regulatory Change Record</h3>
            </div>
            <form onSubmit={handleCreateChangeSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Change Reference</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. CHG-2026-002"
                    value={changeRef}
                    onChange={(e) => setChangeRef(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Effective Date</label>
                  <input
                    type="date"
                    required
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. EASA ED Decision: Flight Operations Standards Revision"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Impact Assessment Summary</label>
                <textarea
                  rows={2}
                  value={impactAssessment}
                  onChange={(e) => setImpactAssessment(e.target.value)}
                  placeholder="Impact on operational manuals, procedures, and training syllabi..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Impact Triggers</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={requiresMoc}
                    onChange={(e) => setRequiresMoc(e.target.checked)}
                  />
                  <span>Management of Change (MoC) Required</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={requiresDoc}
                    onChange={(e) => setRequiresDoc(e.target.checked)}
                  />
                  <span>Operational Manual / Document Revision Required</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={requiresTraining}
                    onChange={(e) => setRequiresTraining(e.target.checked)}
                  />
                  <span>Personnel Training Required</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={requiresAudit}
                    onChange={(e) => setRequiresAudit(e.target.checked)}
                  />
                  <span>Special Compliance Audit Required</span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                  style={{ fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ fontSize: '13px' }}
                >
                  Create Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: New Action */}
      {showActionModal && selectedChange && (
        <div className="modal-backdrop">
          <div className="card" style={{ maxWidth: '480px', width: '100%', margin: '20px' }}>
            <div className="card-header">
              <h3 className="card-title">Add Work Action Package</h3>
            </div>
            <form onSubmit={handleAddActionSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Action Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Flight Crew Operations Manual Annex-2 Update"
                  value={actionTitle}
                  onChange={(e) => setActionTitle(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Action Type</label>
                  <select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value as any)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                  >
                    <option value="MANUAL_UPDATE">Manual Update</option>
                    <option value="POLICY_AMENDMENT">Policy Amendment</option>
                    <option value="TRAINING_SESSION">Training Session</option>
                    <option value="AUDIT_SCHEDULING">Audit Scheduling</option>
                    <option value="TECH_SPEC">Technical Specification</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Due Date</label>
                  <input
                    type="date"
                    required
                    value={actionDueDate}
                    onChange={(e) => setActionDueDate(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Department</label>
                  <input
                    type="text"
                    required
                    value={actionDept}
                    onChange={(e) => setActionDept(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Assignee</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. John Doe"
                    value={actionAssignee}
                    onChange={(e) => setActionAssignee(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => setShowActionModal(false)}
                  className="btn-secondary"
                  style={{ fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ fontSize: '13px' }}
                >
                  Save Action
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
